import BigNumber from 'bignumber.js'
import { isChineseLocale, intlLocaleFor } from './lang/locales.mjs'

function storage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch (_e) {
    return null
  }
}

export const db = {
  get(k, def = '') {
    const ls = storage()
    if (!ls) return def
    try {
      const v = ls.getItem(k)
      return v ? v : def
    } catch (_e) {
      return def
    }
  },
  set(k, v) {
    const ls = storage()
    if (!ls) return
    try {
      ls.setItem(k, v)
    } catch (_e) {
      void _e
    }
  },
}

/** 安全把 chainId（number / string / 0x / bigint）转为 number，避免 Number(bigint) / Math.* 抛错 */
export function coerceChainId(raw, fallback = 0) {
  if (raw == null || raw === '') return fallback
  if (typeof raw === 'bigint') return parseInt(raw.toString(), 10) || fallback
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (!s) return fallback
    // CAIP-2，如 eip155:201022
    if (s.includes(':')) {
      const tail = s.slice(s.lastIndexOf(':') + 1)
      if (tail) {
        const caipBase = tail.startsWith('0x') || tail.startsWith('0X') ? 16 : 10
        const caipN = parseInt(tail, caipBase)
        if (Number.isFinite(caipN)) return caipN
      }
    }
    const base = s.startsWith('0x') || s.startsWith('0X') ? 16 : 10
    const n = parseInt(s, base)
    return Number.isFinite(n) ? n : fallback
  }
  try {
    const n = Number(raw)
    return Number.isFinite(n) ? n : fallback
  } catch {
    return fallback
  }
}

export const formatBalance = (v, decimals = 18) => {
  if(!v) return ''
  return Number(v.div(new BigNumber(10**decimals)).toFixed(3, 1)).toLocaleString()
}

/**
 * 顶栏等窄位：最多 2 位小数；≥1万用紧凑记数。
 * @param {string} appLang 应用语言：`'en'` 强制 en-US（K/M）；`'zh'` 用 zh-CN（万 等）
 */
export const formatBalanceHeaderUsdt = (v, decimals = 18, appLang = 'en') => {
  if(v == null || v === '') return '0'
  let n
  try{
    n = new BigNumber(v).div(new BigNumber(10).pow(decimals))
  }catch(_e){
    return '0'
  }
  if(!n.isFinite() || n.isNaN() || n.lte(0)) return '0'
  if(n.gte(10000)){
    const x = Number(n.toFixed(12))
    if(!Number.isFinite(x)) return n.decimalPlaces(2, BigNumber.ROUND_DOWN).toFixed(2)
    const intlLocale = intlLocaleFor(appLang)
    try{
      return new Intl.NumberFormat(intlLocale, { notation: 'compact', maximumFractionDigits: 2 }).format(x)
    }catch(_e){
      if(isChineseLocale(appLang) && n.gte(1e4)){
        return `${n.div(1e4).decimalPlaces(2, BigNumber.ROUND_DOWN).toFixed()}万`
      }
      if(n.gte(1e6)) return `${n.div(1e6).decimalPlaces(2, BigNumber.ROUND_DOWN).toFixed()}M`
      return `${n.div(1e3).decimalPlaces(2, BigNumber.ROUND_DOWN).toFixed()}K`
    }
  }
  return n.decimalPlaces(2, BigNumber.ROUND_DOWN).toFixed(2)
}

/** 界面展示：稳定币 USDT 用 $，其它代币保持原符号 */
export const USD_DISPLAY_UNIT = '$'

export function displayTokenUnit(symbol) {
  const s = String(symbol ?? 'USDT').trim().toUpperCase()
  if (s === 'USDT') return USD_DISPLAY_UNIT
  return String(symbol ?? '').trim() || USD_DISPLAY_UNIT
}

/**
 * 交易确认弹窗等场景：Uniswap 式展示精度
 * - 稳定币/USDT：按数量级限制小数位，ROUND_DOWN，去掉尾随 0
 * - 链上仍用完整精度，此处仅影响 UI
 */
export function formatTradeAmountDisplay(value, opts = {}) {
  const symbol = String(opts.symbol ?? 'USDT').trim().toUpperCase()
  const isStable = symbol === 'USDT' || symbol === 'USDC' || symbol === 'DAI' || symbol === 'BUSD'
  let n
  try {
    n = new BigNumber(String(value ?? '').trim())
  } catch (_e) {
    return String(value ?? '').trim() || '0'
  }
  if (!n.isFinite() || n.isNaN() || n.lte(0)) return '0'

  let maxDp = Number(opts.maxDecimals)
  if (!Number.isFinite(maxDp)) {
    if (isStable) {
      if (n.gte(1e6)) maxDp = 2
      else if (n.gte(1e4)) maxDp = 4
      else if (n.gte(1)) maxDp = 6
      else maxDp = 6
    } else {
      maxDp = n.gte(1) ? 6 : 8
    }
  }

  let s = n.decimalPlaces(maxDp, BigNumber.ROUND_DOWN).toFixed(maxDp)
  if (s.includes('.')) s = s.replace(/\.?0+$/, '')
  return s
}

/** 弹窗长数字：超宽时省略中间，保留完整值在 title */
export function formatTradeAmountCompact(value, opts = {}) {
  const full = formatTradeAmountDisplay(value, opts)
  const maxLen = Number(opts.maxLen ?? 14)
  if (full.length <= maxLen) return { text: full, full }
  const parts = full.split('.')
  if (parts.length === 2 && parts[0].length + parts[1].length > maxLen) {
    const intLen = Math.min(parts[0].length, 8)
    const fracLen = Math.max(2, maxLen - intLen - 1)
    const trimmed = `${parts[0].slice(0, intLen)}.${parts[1].slice(0, fracLen)}`
    return { text: trimmed, full }
  }
  return { text: `${full.slice(0, maxLen - 1)}…`, full }
}

export const sleep = s => {
  return new Promise(r => {
    setTimeout(r, s * 1000)
  })
}

export const api = (path, data) => {
  return new Promise((resolve, reject) => {
    fetch(path,
    (
    data
    ? {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      }
    } 
    :undefined
    )
    ).then(res=>res.json())
    .then(res => {
      if(res.code != 1){
        console.warn(res.msg)
        return reject(res.msg)
      }
      resolve(res.data)
    })
  })
}
